import { useDataExplorerAppearance } from './appearanceContext';
import { DataExplorerDetailSection } from './DataExplorerDetailSection';
import type { DataExplorerDetailShellProps } from './types';
import { resolveDataExplorerAppearance } from './theme';
import { cn, resolveDataExplorerValue } from './utils';

function DefaultCloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function DefaultChevronLeftIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function DefaultChevronRightIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

export function DataExplorerDetailShell<TContext = unknown>({
  title,
  subtitle,
  leading,
  actions,
  preview,
  previewVisible = true,
  previewStyle,
  previewPadded = false,
  previewCentered = true,
  sections = [],
  rightPanel,
  context,
  navigation,
  closeLabel = 'Close',
  closeIcon,
  closeVisible = true,
  onClose,
  appearance,
  className,
  classNames,
}: DataExplorerDetailShellProps<TContext>) {
  const theme = resolveDataExplorerAppearance(
    useDataExplorerAppearance(appearance),
  );
  const hasNavigation =
    (navigation?.visible ?? true) &&
    typeof navigation?.count === 'number' &&
    navigation.count > 1;
  const visibleSections = sections.filter(
    (section) =>
      !(resolveDataExplorerValue(section.hidden, context) ?? false),
  );

  return (
    <div
      className={cn(
        theme.slots.detailRoot,
        classNames?.root,
        className,
      )}
      style={theme.styles.detailRoot}
    >
      <div
        className={cn(
          theme.slots.detailHeader,
          classNames?.header,
        )}
      >
        <div className={cn('flex min-w-0 items-center gap-3', classNames?.headerMain)}>
          {leading ? (
            <div
              className={cn(
                theme.slots.detailLeading,
                classNames?.leading,
              )}
            >
              {leading}
            </div>
          ) : null}
          <div className={cn('min-w-0', classNames?.titleGroup)}>
            <div
              className={cn(
                'truncate text-[20px] font-semibold leading-tight text-foreground',
                classNames?.title,
              )}
            >
              {title}
            </div>
            {subtitle ? (
              <div
                className={cn(
                  'mt-1 min-w-0 truncate text-sm text-muted-foreground',
                  classNames?.subtitle,
                )}
              >
                {subtitle}
              </div>
            ) : null}
          </div>
        </div>

        <div className={cn('flex items-center gap-2', classNames?.headerActions)}>
          {actions}
          {hasNavigation ? (
            <div
              className={cn(
                theme.slots.detailNavigation,
                classNames?.navigation,
              )}
            >
              <button
                type="button"
                aria-label={navigation?.previousLabel || 'Previous'}
                className={cn(
                  theme.slots.detailNavigationButton,
                  classNames?.navigationButton,
                )}
                disabled={!navigation?.onPrevious}
                onClick={navigation?.onPrevious}
              >
                <DefaultChevronLeftIcon />
              </button>
              <div
                className={cn(
                  'min-w-[74px] text-center text-xs font-medium text-muted-foreground',
                  classNames?.navigationCount,
                )}
              >
                {navigation.index + 1} / {navigation.count}
              </div>
              <button
                type="button"
                aria-label={navigation?.nextLabel || 'Next'}
                className={cn(
                  theme.slots.detailNavigationButton,
                  classNames?.navigationButton,
                )}
                disabled={!navigation?.onNext}
                onClick={navigation?.onNext}
              >
                <DefaultChevronRightIcon />
              </button>
            </div>
          ) : null}

          {closeVisible ? (
            <button
              type="button"
              aria-label={closeLabel}
              className={cn(
                theme.slots.detailCloseButton,
                classNames?.closeButton,
              )}
              onClick={onClose}
            >
              {closeIcon ?? <DefaultCloseIcon />}
            </button>
          ) : null}
        </div>
      </div>

      <div
        className={cn(
          'grid min-h-0 flex-1',
          previewVisible
            ? 'xl:grid-cols-[minmax(0,1fr)_380px] 2xl:grid-cols-[minmax(0,1fr)_400px]'
            : 'grid-cols-1',
          classNames?.body,
        )}
      >
        {previewVisible ? (
          <div
            className={cn(
              theme.slots.detailPreviewPanel,
              classNames?.previewPanel,
            )}
          >
          <div
            className={cn(
              'flex h-full min-h-0 flex-col',
              classNames?.previewStack,
            )}
          >
            {hasNavigation ? (
              <div className="flex justify-end">
                <div
                  className={cn(
                    'text-xs text-muted-foreground xl:hidden',
                    classNames?.previewMobileCount,
                  )}
                >
                  {navigation.index + 1} / {navigation.count}
                </div>
              </div>
            ) : null}

            <div
              className={cn(
                'flex min-h-0 flex-1 justify-center',
                previewCentered && 'items-stretch',
                classNames?.previewWrapper,
              )}
            >
              <div
                className={cn(
                  theme.slots.detailPreviewFrame,
                  classNames?.previewFrame,
                )}
                style={previewStyle}
              >
                <div
                  className={cn(
                    'relative flex h-full w-full items-center justify-center',
                    previewPadded && 'p-4',
                    classNames?.previewContent,
                  )}
                >
                  {preview}
                </div>

                {hasNavigation ? (
                  <>
                    <button
                      type="button"
                      aria-label={navigation?.previousLabel || 'Previous'}
                      className={cn(
                        theme.slots.previewNavigationButton,
                        'left-4',
                        classNames?.previewNavigationButton,
                      )}
                      disabled={!navigation?.onPrevious}
                      onClick={navigation?.onPrevious}
                    >
                      <DefaultChevronLeftIcon />
                    </button>
                    <button
                      type="button"
                      aria-label={navigation?.nextLabel || 'Next'}
                      className={cn(
                        theme.slots.previewNavigationButton,
                        'right-4',
                        classNames?.previewNavigationButton,
                      )}
                      disabled={!navigation?.onNext}
                      onClick={navigation?.onNext}
                    >
                      <DefaultChevronRightIcon />
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>
          </div>
        ) : null}

        <div
          className={cn(
            theme.slots.detailRightPanel,
            classNames?.rightPanel,
          )}
        >
          <div
            className={cn(
              theme.slots.detailRightPanelBody,
              classNames?.rightPanelBody,
            )}
            style={{ scrollbarGutter: 'stable' }}
          >
            {rightPanel ?? (
              <div className={cn('space-y-6', classNames?.sections)}>
                {visibleSections.map((section) => (
                  <DataExplorerDetailSection
                    key={section.id}
                    section={section}
                    context={context}
                    className={classNames?.section}
                    fieldClassNames={{ root: classNames?.field }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
